import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, FileImage, FileText, Phone, QrCode, Loader2, CreditCard, CheckCircle } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Standard PVC Card Dimensions (ISO/IEC 7810 ID-1)
const CARD_CONFIG = {
  displayWidth: 380,      // Increased from 340 for better visibility
  displayHeight: 240,     // Increased from 215 for better proportions
  printWidth: 1020,
  printHeight: 644,
  physicalWidthMm: 85.6,
  physicalHeightMm: 53.98,
  physicalWidthIn: 3.370,
  physicalHeightIn: 2.125,
  borderRadius: 14,       // Slightly larger radius
  printBorderRadius: 36,
  qrSize: 72,             // Increased QR code size for display
  printQrSize: 180,       // Increased QR code size for print
};

const formatDate = (dateString, defaultText = 'N/A') => {
  if (!dateString) return defaultText;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime()) || date.getFullYear() < 2000) return defaultText;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return defaultText;
  }
};

// Pure Canvas rendering function - NO html2canvas
const renderCardToCanvas = async (cardInfo, qrCodeDataUrl, isFront = true) => {
  const width = CARD_CONFIG.printWidth;
  const height = CARD_CONFIG.printHeight;
  const radius = CARD_CONFIG.printBorderRadius;
  
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  
  // Helper: Draw rounded rectangle
  const roundRect = (x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  };
  
  // Helper: Load image with CORS
  const loadImage = (src) => {
    return new Promise((resolve) => {
      if (!src) {
        resolve(null);
        return;
      }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = src;
    });
  };

  if (isFront) {
    // === FRONT CARD ===
    // Enhanced background gradient - more vibrant and modern
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    const primaryColor = cardInfo.primaryColor || '#0d9488';
    const secondaryColor = cardInfo.secondaryColor || '#14b8a6';
    
    // Create a richer gradient with an accent color
    gradient.addColorStop(0, primaryColor);
    gradient.addColorStop(0.5, secondaryColor);
    gradient.addColorStop(1, primaryColor);
    
    roundRect(0, 0, width, height, radius);
    ctx.fillStyle = gradient;
    ctx.fill();
    
    // Add subtle overlay pattern for depth
    ctx.fillStyle = 'rgba(255,255,255,0.03)';
    for (let i = 0; i < width; i += 40) {
      ctx.fillRect(i, 0, 20, height);
    }
    
    // Try to load clinic logo
    const logoImg = await loadImage(cardInfo.logoUrl);
    
    if (logoImg) {
      // Draw white background for logo
      ctx.fillStyle = '#ffffff';
      roundRect(36, 36, 100, 100, 14);
      ctx.fill();
      
      // Draw logo image
      ctx.save();
      roundRect(42, 42, 88, 88, 10);
      ctx.clip();
      ctx.drawImage(logoImg, 42, 42, 88, 88);
      ctx.restore();
    } else {
      // Fallback: Draw initial letter with better styling
      ctx.fillStyle = 'rgba(255,255,255,0.2)';
      roundRect(36, 36, 100, 100, 14);
      ctx.fill();
      
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 52px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(cardInfo.clinicName.charAt(0), 86, 86);
    }
    
    // Clinic name - LARGER font
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 42px Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    const maxClinicWidth = width - 200 - 200; // Leave space for QR
    let clinicName = cardInfo.clinicName;
    while (ctx.measureText(clinicName).width > maxClinicWidth && clinicName.length > 10) {
      clinicName = clinicName.substring(0, clinicName.length - 1);
    }
    if (clinicName !== cardInfo.clinicName) clinicName += '...';
    ctx.fillText(clinicName, 156, 40);
    
    // MEMBER CARD label - LARGER
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.font = '600 30px Arial, sans-serif';
    ctx.fillText('MEMBER CARD', 156, 90);
    
    // QR Code - BIGGER (180px instead of 156px)
    if (qrCodeDataUrl) {
      const qrImg = new Image();
      qrImg.src = qrCodeDataUrl;
      await new Promise((resolve) => {
        qrImg.onload = resolve;
        qrImg.onerror = resolve;
      });
      
      // White background for QR - LARGER
      const qrBgSize = CARD_CONFIG.printQrSize + 24;
      ctx.fillStyle = '#ffffff';
      roundRect(width - qrBgSize - 24, 20, qrBgSize, qrBgSize, 14);
      ctx.fill();
      
      ctx.drawImage(qrImg, width - CARD_CONFIG.printQrSize - 36, 32, CARD_CONFIG.printQrSize, CARD_CONFIG.printQrSize);
    }
    
    // Member Name label - LARGER
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.font = '500 30px Arial, sans-serif';
    ctx.fillText('Member Name', 36, 175);
    
    // Member Name value - LARGER and bolder
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 54px Arial, sans-serif';
    const memberName = cardInfo.memberName.toUpperCase();
    const maxNameWidth = width - 72;
    let displayName = memberName;
    while (ctx.measureText(displayName).width > maxNameWidth && displayName.length > 10) {
      displayName = displayName.substring(0, displayName.length - 1);
    }
    if (displayName !== memberName) displayName += '...';
    ctx.fillText(displayName, 36, 215);
    
    // ID label - LARGER
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.font = '500 28px Arial, sans-serif';
    ctx.fillText('ID', 36, 295);
    
    // ID value - LARGER
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 34px Courier New, monospace';
    ctx.fillText(cardInfo.patientId, 36, 332);
    
    // Plan label - LARGER
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.font = '500 28px Arial, sans-serif';
    ctx.fillText('Plan', 300, 295);
    
    // Plan value - LARGER
    ctx.fillStyle = '#ffffff';
    ctx.font = '700 32px Arial, sans-serif';
    const maxPlanWidth = width - 300 - 50;
    let planName = cardInfo.planName;
    while (ctx.measureText(planName).width > maxPlanWidth && planName.length > 10) {
      planName = planName.substring(0, planName.length - 1);
    }
    if (planName !== cardInfo.planName) planName += '...';
    ctx.fillText(planName, 300, 332);
    
    // Footer divider - thicker
    ctx.strokeStyle = 'rgba(255,255,255,0.4)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(36, height - 130);
    ctx.lineTo(width - 36, height - 130);
    ctx.stroke();
    
    // Valid Until label - LARGER
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.font = '500 28px Arial, sans-serif';
    ctx.fillText('Valid Until', 36, height - 105);
    
    // Valid Until value - LARGER
    ctx.fillStyle = '#ffffff';
    ctx.font = '700 38px Arial, sans-serif';
    ctx.fillText(cardInfo.validUntil, 36, height - 60);
    
    // Phone box - LARGER
    if (cardInfo.phone) {
      const phoneText = cardInfo.phone;
      ctx.font = '700 34px Arial, sans-serif';
      const phoneWidth = ctx.measureText(phoneText).width + 60;
      
      // Draw phone background box
      ctx.fillStyle = 'rgba(255,255,255,0.25)';
      roundRect(width - phoneWidth - 36, height - 110, phoneWidth, 55, 12);
      ctx.fill();
      
      // Draw phone text
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.fillText(phoneText, width - phoneWidth/2 - 36, height - 70);
      ctx.textAlign = 'left';
    }
    
  } else {
    // === BACK CARD ===
    // Dark background
    const bgGradient = ctx.createLinearGradient(0, 0, 0, height);
    bgGradient.addColorStop(0, '#1a1a2e');
    bgGradient.addColorStop(1, '#16213e');
    
    roundRect(0, 0, width, height, radius);
    ctx.fillStyle = bgGradient;
    ctx.fill();
    
    // Magnetic stripe
    ctx.fillStyle = '#111827';
    ctx.fillRect(0, 24, width, 84);
    
    // Emergency Contact box
    ctx.fillStyle = 'rgba(255,255,255,0.1)';
    roundRect(36, 132, 400, 90, 12);
    ctx.fill();
    
    ctx.fillStyle = '#d1d5db';
    ctx.font = '600 30px Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText('Emergency Contact', 54, 147);
    
    ctx.fillStyle = '#ffffff';
    ctx.font = '500 33px Arial, sans-serif';
    ctx.fillText(cardInfo.phone || 'Contact clinic', 54, 183);
    
    // Card ID
    ctx.fillStyle = '#6b7280';
    ctx.font = '400 24px Arial, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('Card ID', width - 48, 144);
    
    ctx.fillStyle = '#9ca3af';
    ctx.font = '400 30px Courier New, monospace';
    ctx.fillText(cardInfo.patientId, width - 48, 177);
    
    // Terms box
    ctx.fillStyle = 'rgba(17,24,39,0.5)';
    roundRect(36, 240, width - 72, 300, 12);
    ctx.fill();
    
    ctx.fillStyle = '#d1d5db';
    ctx.font = '600 24px Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('TERMS & CONDITIONS', 54, 264);
    
    ctx.fillStyle = '#6b7280';
    ctx.font = '400 21px Arial, sans-serif';
    const terms = [
      `• Valid exclusively at ${cardInfo.clinicName}. Non-transferable.`,
      '• Benefits subject to plan terms. Present card at each visit.',
      '• Clinic may verify status. Report lost cards immediately.',
      '• Service availability subject to scheduling.'
    ];
    let termY = 300;
    terms.forEach(term => {
      const displayTerm = term.length > 65 ? term.substring(0, 65) + '...' : term;
      ctx.fillText(displayTerm, 54, termY);
      termY += 36;
    });
    
    // Footer
    ctx.fillStyle = '#4b5563';
    ctx.font = '400 21px Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('Powered by MyMedMember', 36, height - 24);
    ctx.textAlign = 'right';
    ctx.fillText('mymedmember.com', width - 36, height - 24);
  }
  
  return canvas;
};

const MembershipCard = ({ member, membershipPlan, clinicBranding }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [showBack, setShowBack] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (member?.id) {
      fetchCardData();
    }
  }, [member?.id]);

  const fetchCardData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/members/${member.id}/membership-card-data`);
      setCardData(response.data);
      
      // Fetch QR code as blob and convert to data URL
      const currentOrigin = window.location.origin;
      const qrResponse = await axios.get(`${API_URL}/api/members/${member.id}/qr-code`, {
        params: { origin: currentOrigin },
        responseType: 'blob'
      });
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setQrCodeUrl(reader.result);
      };
      reader.readAsDataURL(qrResponse.data);
      
    } catch (error) {
      console.error('Failed to fetch card data:', error);
      toast.error('Failed to load membership card data');
    } finally {
      setLoading(false);
    }
  };

  const getMemberName = () => {
    if (cardData?.member?.full_name) return cardData.member.full_name;
    return member.full_name || member.name || 'Member';
  };

  const getPatientId = () => {
    return cardData?.member?.patient_id || member.patient_id || member.id?.substring(0, 8).toUpperCase();
  };

  const getClinicName = () => {
    return cardData?.clinic?.name || clinicBranding?.name || 'Medical Center';
  };

  const getClinicPhone = () => {
    return cardData?.clinic?.phone || clinicBranding?.phone || '';
  };

  const getClinicLogo = () => {
    return cardData?.clinic?.logo_url || clinicBranding?.logo_url || null;
  };

  const getClinicAddress = () => {
    return cardData?.clinic?.address || clinicBranding?.address || '';
  };

  const getClinicWebsite = () => {
    return cardData?.clinic?.website || clinicBranding?.website || '';
  };

  const getPrimaryColor = () => {
    // Prefer card-specific colors, fallback to general primary color
    return cardData?.clinic?.card_primary_color || cardData?.clinic?.primary_color || clinicBranding?.card_primary_color || clinicBranding?.primary_color || '#0d9488';
  };

  const getSecondaryColor = () => {
    // Prefer card-specific colors, fallback to general secondary color
    return cardData?.clinic?.card_secondary_color || cardData?.clinic?.secondary_color || clinicBranding?.card_secondary_color || clinicBranding?.secondary_color || '#14b8a6';
  };

  // Get card info object for Canvas rendering
  const getCardInfo = () => ({
    clinicName: getClinicName(),
    memberName: getMemberName(),
    patientId: getPatientId(),
    planName: membershipPlan?.name || 'Membership Plan',
    validUntil: formatDate(member.membership_end_date, 'Active'),
    phone: getClinicPhone(),
    primaryColor: getPrimaryColor(),
    secondaryColor: getSecondaryColor(),
    logoUrl: getClinicLogo()
  });

  const downloadAsPNG = async (side = 'both') => {
    try {
      setDownloading('png');
      toast.info('Generating high-resolution PNG...');
      
      const cardInfo = getCardInfo();
      
      if (side === 'front' || side === 'both') {
        const frontCanvas = await renderCardToCanvas(cardInfo, qrCodeUrl, true);
        const link = document.createElement('a');
        link.download = `membership-card-${getPatientId()}-FRONT.png`;
        link.href = frontCanvas.toDataURL('image/png', 1.0);
        link.click();
      }
      
      if (side === 'back' || side === 'both') {
        await new Promise(resolve => setTimeout(resolve, 300));
        const backCanvas = await renderCardToCanvas(cardInfo, qrCodeUrl, false);
        const link = document.createElement('a');
        link.download = `membership-card-${getPatientId()}-BACK.png`;
        link.href = backCanvas.toDataURL('image/png', 1.0);
        link.click();
      }
      
      toast.success(side === 'both' ? 'Both sides downloaded!' : `${side.toUpperCase()} side downloaded!`);
    } catch (error) {
      console.error('PNG download error:', error);
      toast.error(`Failed to download PNG: ${error.message}`);
    } finally {
      setDownloading(null);
    }
  };

  const downloadAsPDF = async () => {
    try {
      setDownloading('pdf');
      toast.info('Generating print-ready PDF...');
      
      const cardInfo = getCardInfo();
      
      const frontCanvas = await renderCardToCanvas(cardInfo, qrCodeUrl, true);
      const backCanvas = await renderCardToCanvas(cardInfo, qrCodeUrl, false);
      
      const frontImgData = frontCanvas.toDataURL('image/png', 1.0);
      const backImgData = backCanvas.toDataURL('image/png', 1.0);
      
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: [CARD_CONFIG.physicalWidthMm, CARD_CONFIG.physicalHeightMm]
      });
      
      pdf.addImage(frontImgData, 'PNG', 0, 0, CARD_CONFIG.physicalWidthMm, CARD_CONFIG.physicalHeightMm, undefined, 'FAST');
      pdf.addPage([CARD_CONFIG.physicalWidthMm, CARD_CONFIG.physicalHeightMm], 'landscape');
      pdf.addImage(backImgData, 'PNG', 0, 0, CARD_CONFIG.physicalWidthMm, CARD_CONFIG.physicalHeightMm, undefined, 'FAST');
      
      pdf.save(`membership-card-${getPatientId()}.pdf`);
      toast.success('PDF downloaded with FRONT and BACK!');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(`Failed to download PDF: ${error.message}`);
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading membership card...</p>
        </CardContent>
      </Card>
    );
  }

  if (!member || !membershipPlan) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-gray-600">No active membership found</p>
        </CardContent>
      </Card>
    );
  }

  const primaryColor = getPrimaryColor();
  const secondaryColor = getSecondaryColor();
  const clinicLogo = getClinicLogo();
  const clinicPhone = getClinicPhone();
  const clinicAddress = getClinicAddress();

  // Card Front Component
  const CardFront = () => {
    const width = CARD_CONFIG.displayWidth;
    const height = CARD_CONFIG.displayHeight;
    const primaryColor = getPrimaryColor();
    const secondaryColor = getSecondaryColor();
    
    // Simple inline styles
    const textStyle = {
      fontFamily: 'Arial, sans-serif',
      margin: 0,
      padding: 0,
      textShadow: 'none',
      filter: 'none',
      transform: 'none'
    };
    
    return (
      <div 
        style={{
          width: `${width}px`,
          height: `${height}px`,
          borderRadius: `${CARD_CONFIG.borderRadius}px`,
          background: `linear-gradient(145deg, ${primaryColor} 0%, ${secondaryColor} 50%, ${primaryColor} 100%)`,
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {/* Subtle pattern overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'repeating-linear-gradient(90deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 20px, transparent 20px, transparent 40px)',
          pointerEvents: 'none'
        }} />
        
        {/* Card Content */}
        <div style={{ 
          position: 'relative', 
          zIndex: 10, 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'space-between', 
          padding: '14px',
          color: 'white',
          fontFamily: 'Arial, sans-serif'
        }}>
          {/* Header Row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            {/* Clinic Info */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
              {clinicLogo ? (
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  backgroundColor: 'white', 
                  borderRadius: '6px', 
                  padding: '3px',
                  flexShrink: 0
                }}>
                  <img 
                    src={clinicLogo} 
                    alt="Logo" 
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    crossOrigin="anonymous"
                  />
                </div>
              ) : (
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  backgroundColor: 'rgba(255,255,255,0.2)', 
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <span style={{ ...textStyle, fontWeight: 700, fontSize: '20px' }}>{getClinicName().charAt(0)}</span>
                </div>
              )}
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ 
                  ...textStyle,
                  fontWeight: 700, 
                  fontSize: '15px', 
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '170px'
                }}>
                  {getClinicName()}
                </p>
                <p style={{ 
                  ...textStyle,
                  fontSize: '11px', 
                  opacity: 0.9, 
                  fontWeight: 600,
                  marginTop: '3px',
                  letterSpacing: '0.5px'
                }}>MEMBER CARD</p>
              </div>
            </div>
            
            {/* QR Code - BIGGER */}
            <div style={{ 
              backgroundColor: 'white', 
              padding: '5px', 
              borderRadius: '6px',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}>
              {qrCodeUrl ? (
                <img 
                  src={qrCodeUrl} 
                  alt="QR" 
                  style={{ width: `${CARD_CONFIG.qrSize}px`, height: `${CARD_CONFIG.qrSize}px`, display: 'block' }}
                  crossOrigin="anonymous"
                />
              ) : (
                <div style={{ width: `${CARD_CONFIG.qrSize}px`, height: `${CARD_CONFIG.qrSize}px`, backgroundColor: '#f3f4f6', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <QrCode style={{ width: '32px', height: '32px', color: '#9ca3af' }} />
                </div>
              )}
            </div>
          </div>

          {/* Member Info - Center */}
          <div style={{ paddingTop: '10px', paddingBottom: '10px' }}>
            <p style={{ 
              ...textStyle,
              fontSize: '11px', 
              opacity: 0.9, 
              textTransform: 'uppercase', 
              marginBottom: '4px',
              fontWeight: 500,
              letterSpacing: '0.5px'
            }}>Member Name</p>
            <p style={{ 
              ...textStyle,
              fontWeight: 700, 
              fontSize: '22px', 
              lineHeight: 1,
              letterSpacing: '0.5px'
            }}>
              {getMemberName().toUpperCase()}
            </p>
            
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px', marginTop: '12px' }}>
              <div>
                <p style={{ 
                  ...textStyle,
                  fontSize: '11px', 
                  opacity: 0.9, 
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  marginBottom: '3px',
                  letterSpacing: '0.5px'
                }}>ID</p>
                <p style={{ 
                  ...textStyle,
                  fontFamily: 'Courier New, monospace', 
                  fontWeight: 700, 
                  fontSize: '14px'
                }}>{getPatientId()}</p>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ 
                  ...textStyle,
                  fontSize: '11px', 
                  opacity: 0.9, 
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  marginBottom: '3px',
                  letterSpacing: '0.5px'
                }}>Plan</p>
                <p style={{ 
                  ...textStyle,
                  fontWeight: 700, 
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '160px'
                }}>
                  {membershipPlan.name}
                </p>
              </div>
            </div>
          </div>

          {/* Footer Row */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-end', 
            paddingTop: '8px', 
            borderTop: '2px solid rgba(255,255,255,0.35)' 
          }}>
            <div>
              <p style={{ 
                ...textStyle,
                fontSize: '11px', 
                opacity: 0.9, 
                textTransform: 'uppercase',
                marginBottom: '3px',
                letterSpacing: '0.5px'
              }}>Valid Until</p>
              <p style={{ 
                ...textStyle,
                fontWeight: 700, 
                fontSize: '14px'
              }}>{formatDate(member.membership_end_date, 'Active')}</p>
            </div>
            
            {/* Clinic Phone */}
            {clinicPhone && (
              <div style={{ 
                backgroundColor: 'rgba(255,255,255,0.25)', 
                borderRadius: '6px', 
                padding: '6px 10px' 
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Phone style={{ width: '13px', height: '13px' }} />
                  <span style={{ 
                    ...textStyle,
                    fontWeight: 700, 
                    fontSize: '13px' 
                  }}>{clinicPhone}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Card Back Component
  const CardBack = () => {
    const width = CARD_CONFIG.displayWidth;
    const height = CARD_CONFIG.displayHeight;
    
    return (
      <div 
        style={{
          width: `${width}px`,
          height: `${height}px`,
          borderRadius: `${CARD_CONFIG.borderRadius}px`,
          background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          overflow: 'hidden',
          position: 'relative',
          fontFamily: 'Arial, Helvetica, sans-serif',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'geometricPrecision'
        }}
      >
        {/* Magnetic Stripe */}
        <div style={{ width: '100%', height: '32px', backgroundColor: '#111827', marginTop: '10px' }} />
        
        {/* Content */}
        <div style={{ padding: '8px 14px', color: 'white' }}>
          {/* Emergency Contact Row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ backgroundColor: 'rgba(255,255,255,0.1)', padding: '6px 10px', borderRadius: '6px' }}>
              <p style={{ fontWeight: 600, fontSize: '12px', color: '#d1d5db', margin: 0 }}>Emergency Contact</p>
              {clinicPhone ? (
                <p style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: 600, margin: 0, marginTop: '3px' }}>
                  <Phone style={{ width: '12px', height: '12px' }} /> {clinicPhone}
                </p>
              ) : (
                <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>Contact clinic</p>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', margin: 0, letterSpacing: '0.5px' }}>Card ID</p>
              <p style={{ fontFamily: 'Courier New, Courier, monospace', fontSize: '12px', color: '#9ca3af', margin: 0, marginTop: '3px', fontWeight: 600 }}>{getPatientId()}</p>
            </div>
          </div>
          
          {/* Terms and Conditions */}
          <div style={{ backgroundColor: 'rgba(17,24,39,0.5)', borderRadius: '6px', padding: '8px' }}>
            <p style={{ fontWeight: 700, fontSize: '10px', color: '#d1d5db', margin: 0, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Terms & Conditions</p>
            <div style={{ color: '#6b7280', fontSize: '9px', lineHeight: 1.5 }}>
              <p style={{ margin: 0, marginBottom: '3px' }}>• Valid exclusively at <span style={{ color: '#9ca3af', fontWeight: 500 }}>{getClinicName()}</span>. Non-transferable.</p>
              <p style={{ margin: 0, marginBottom: '3px' }}>• Benefits subject to plan terms. Present card at each visit.</p>
              <p style={{ margin: 0, marginBottom: '3px' }}>• Clinic may verify status. Report lost cards immediately.</p>
              <p style={{ margin: 0 }}>• Service availability subject to scheduling.</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ 
          position: 'absolute', 
          bottom: '8px', 
          left: '14px', 
          right: '14px', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <span style={{ fontSize: '9px', color: '#4b5563', fontWeight: 500 }}>Powered by MyMedMember</span>
          <span style={{ fontSize: '9px', color: '#4b5563', fontWeight: 500 }}>mymedmember.com</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Card Dimension Info */}
      <div className="text-center">
        <Badge variant="outline" className="mb-2">
          <CreditCard className="h-3 w-3 mr-1" />
          Standard PVC Card Size: {CARD_CONFIG.physicalWidthIn}" × {CARD_CONFIG.physicalHeightIn}"
        </Badge>
      </div>

      {/* Card Preview with Flip */}
      <div className="flex flex-col items-center gap-4">
        {/* Card Container */}
        <div 
          className="cursor-pointer transition-transform duration-300 hover:scale-105"
          onClick={() => setShowBack(!showBack)}
          title="Click to flip card"
        >
          <div ref={cardRef}>
            {showBack ? <CardBack /> : <CardFront />}
          </div>
        </div>
        
        <p className="text-sm text-gray-500 flex items-center gap-1">
          <span className="animate-pulse">👆</span> Click card to see {showBack ? 'front' : 'back'}
        </p>
      </div>

      {/* Hidden Print-Quality Cards (for high-res export) */}
      <div className="fixed -left-[9999px] -top-[9999px]" aria-hidden="true" style={{ pointerEvents: 'none' }}>
        <div id="card-print-front" style={{ width: `${CARD_CONFIG.displayWidth}px`, height: `${CARD_CONFIG.displayHeight}px` }}>
          <CardFront />
        </div>
        <div id="card-print-back" style={{ width: `${CARD_CONFIG.displayWidth}px`, height: `${CARD_CONFIG.displayHeight}px`, marginTop: '20px' }}>
          <CardBack />
        </div>
      </div>

      {/* Download Buttons */}
      <div className="space-y-3">
        <div className="flex justify-center gap-3 flex-wrap">
          <Button 
            onClick={() => downloadAsPNG('both')} 
            className="bg-blue-600 hover:bg-blue-700"
            disabled={downloading !== null}
            data-testid="download-png-btn"
          >
            {downloading === 'png' ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileImage className="h-4 w-4 mr-2" />
            )}
            Download PNG (Front + Back)
          </Button>
          <Button 
            onClick={downloadAsPDF} 
            className="bg-teal-600 hover:bg-teal-700"
            disabled={downloading !== null}
            data-testid="download-pdf-btn"
          >
            {downloading === 'pdf' ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 mr-2" />
            )}
            Download PDF (2 Pages)
          </Button>
        </div>
        
        {/* Individual side downloads */}
        <div className="flex justify-center gap-2">
          <Button 
            variant="outline"
            size="sm"
            onClick={() => downloadAsPNG('front')} 
            disabled={downloading !== null}
          >
            <Download className="h-3 w-3 mr-1" />
            Front Only
          </Button>
          <Button 
            variant="outline"
            size="sm"
            onClick={() => downloadAsPNG('back')} 
            disabled={downloading !== null}
          >
            <Download className="h-3 w-3 mr-1" />
            Back Only
          </Button>
        </div>
      </div>

      {/* Print Specifications */}
      <Card className="bg-gradient-to-r from-gray-50 to-blue-50 border-blue-200">
        <CardContent className="p-4">
          <h4 className="font-semibold mb-3 text-gray-800 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-600" />
            Print Specifications for PVC Card
          </h4>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium">Dimensions:</span>
                  <span className="text-gray-600 ml-1">3.37" × 2.13" (85.6mm × 54mm)</span>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium">Resolution:</span>
                  <span className="text-gray-600 ml-1">300 DPI (print quality)</span>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium">Format:</span>
                  <span className="text-gray-600 ml-1">ISO/IEC 7810 ID-1 Standard</span>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium">Corner Radius:</span>
                  <span className="text-gray-600 ml-1">3mm (standard)</span>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium">Compatible:</span>
                  <span className="text-gray-600 ml-1">CR80 PVC card printers</span>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium">Print Scale:</span>
                  <span className="text-gray-600 ml-1">100% (no scaling)</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <h4 className="font-semibold mb-2 text-gray-800">How to Use Your Card:</h4>
          <ul className="text-sm text-gray-600 space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="text-teal-600">•</span>
              <span>Show QR code at clinic check-in for instant verification</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-teal-600">•</span>
              <span>Download PNG for your phone's wallet or photo gallery</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-teal-600">•</span>
              <span>Use PDF for professional PVC card printing (print at 100% scale)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-teal-600">•</span>
              <span>Card fits standard CR80 card holders and badge sleeves</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default MembershipCard;
